import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, ClockIcon, CpuIcon, FileTextIcon, EyeIcon, SendIcon, ChevronLeftIcon, ChevronRightIcon, XIcon, CheckCircleIcon, XCircleIcon, MailIcon, FileIcon, SearchIcon, ArrowUpDownIcon, ArrowUpIcon, ArrowDownIcon, ChevronDownIcon } from 'lucide-react';
import { KpiCard } from '../components/ui/KpiCard';
import { ExamCard } from '../components/ui/ExamCard';
import { MachineStatusIndicator } from '../components/ui/MachineStatusIndicator';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ModalDialog } from '../components/ui/ModalDialog';
import { motion, AnimatePresence } from 'framer-motion';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
// --- Types ---
interface Report {
  id: string;
  patient: string;
  email: string;
  type: string;
  doctor: string;
  date: string;
  status: string;
}
type SortDirection = 'asc' | 'desc' | null;
type SortKey = 'id' | 'patient' | 'type' | 'doctor' | 'status';
// --- Truncated cell with tooltip (disabled on mobile) ---
function TruncatedCell({
  text
}: {
  text: string;
}) {
  const [show, setShow] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const isMobileView = useIsMobile();
  const handleMouseEnter = () => {
    if (isMobileView) return;
    if (textRef.current) {
      if (textRef.current.scrollWidth > textRef.current.clientWidth) {
        setShow(true);
      }
    }
  };
  return <div className="relative min-w-0" onMouseEnter={handleMouseEnter} onMouseLeave={() => setShow(false)} data-id="element-640">
      <span ref={textRef} className="block truncate" data-id="element-641">
        {text}
      </span>
      {show && !isMobileView && <div className="fixed px-2.5 py-1.5 bg-text-primary text-white text-xs rounded-md whitespace-nowrap pointer-events-none shadow-lg" style={{
      zIndex: 9999,
      transform: 'translateY(-100%) translateY(-6px)'
    }} ref={el => {
      if (el) {
        const parent = el.parentElement?.getBoundingClientRect();
        if (parent) {
          el.style.top = `${parent.top}px`;
          el.style.left = `${parent.left}px`;
        }
      }
    }} data-id="element-642">
          {text}
        </div>}
    </div>;
}
// --- Sort header ---
function SortableHeader({
  label,
  sortKey,
  currentSort,
  currentDirection,
  onSort
}: {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey | null;
  currentDirection: SortDirection;
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentSort === sortKey;
  return <button onClick={() => onSort(sortKey)} className="flex items-center gap-1.5 text-table-header text-text-secondary hover:text-text-primary transition-colors w-full group" data-id="element-643">
      <span className="whitespace-nowrap" data-id="element-644">{label}</span>
      <span className="shrink-0 opacity-40 group-hover:opacity-100 transition-opacity" data-id="element-645">
        {isActive && currentDirection === 'asc' ? <ArrowUpIcon size={14} className="text-medical-green" data-id="element-646" /> : isActive && currentDirection === 'desc' ? <ArrowDownIcon size={14} className="text-medical-green" data-id="element-647" /> : <ArrowUpDownIcon size={14} data-id="element-648" />}
      </span>
    </button>;
}
const mockTimeline = [{
  id: 1,
  patientName: 'Marco Rossi',
  examType: 'Risonanza magnetica encefalo',
  time: '08:00',
  doctor: 'Dr. S. Verdi',
  status: 'Refertato'
}, {
  id: 2,
  patientName: 'Giulia Bianchi',
  examType: 'TAC torace',
  time: '09:30',
  doctor: 'Dr. A. Neri',
  status: 'Da refertare'
}, {
  id: 3,
  patientName: 'Luca Romano',
  examType: 'Radiografia colonna vertebrale',
  time: '10:15',
  doctor: 'Dr. M. Gialli',
  status: 'In corso'
}, {
  id: 4,
  patientName: 'Elena Colombo',
  examType: 'Ecografia addome',
  time: '11:00',
  doctor: 'Dr. S. Verdi',
  status: 'In corso'
}, {
  id: 5,
  patientName: 'Alessandro Ricci',
  examType: 'Risonanza magnetica ginocchio',
  time: '11:45',
  doctor: 'Dr. A. Neri',
  status: 'Da refertare'
}, {
  id: 6,
  patientName: 'Sofia Marino',
  examType: 'TAC pelvi',
  time: '13:00',
  doctor: 'Dr. M. Gialli',
  status: 'Da refertare'
}];
const mockMachines = [{
  id: 1,
  name: 'Siemens Magnetom',
  type: 'Risonanza Magnetica',
  status: 'Connesso' as const,
  lastSync: 'Adesso'
}, {
  id: 2,
  name: 'GE Revolution',
  type: 'TAC',
  status: 'Connesso' as const,
  lastSync: '2 min fa'
}, {
  id: 3,
  name: 'Philips Affiniti',
  type: 'Ecografia',
  status: 'Connesso' as const,
  lastSync: '5 min fa'
}, {
  id: 4,
  name: 'Carestream DRX',
  type: 'Radiografia',
  status: 'Manutenzione' as const,
  lastSync: '2 ore fa'
}, {
  id: 5,
  name: 'Hologic Selenia',
  type: 'Mammografia',
  status: 'Connesso' as const,
  lastSync: '1 min fa'
}, {
  id: 6,
  name: 'Hitachi Oasis',
  type: 'Risonanza Magnetica Aperta',
  status: 'Disconnesso' as const,
  lastSync: '1 giorno fa'
}];
const mockReports: Report[] = [{
  id: 'REP-2026-001',
  patient: 'Marco Rossi',
  email: 'marco.rossi@email.it',
  type: 'Risonanza magnetica encefalo',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-10',
  status: 'Refertato'
}, {
  id: 'REP-2026-002',
  patient: 'Anna Ferrari',
  email: 'anna.ferrari@email.it',
  type: 'TAC torace',
  doctor: 'Dr. A. Neri',
  date: '2026-03-10',
  status: 'Refertato'
}, {
  id: 'REP-2026-003',
  patient: 'Giuseppe Russo',
  email: 'giuseppe.russo@email.it',
  type: 'Ecografia addome',
  doctor: 'Dr. M. Gialli',
  date: '2026-03-09',
  status: 'Inviato'
}, {
  id: 'REP-2026-004',
  patient: 'Maria Conti',
  email: 'maria.conti@email.it',
  type: 'Radiografia colonna vertebrale',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-09',
  status: 'Refertato'
}, {
  id: 'REP-2026-005',
  patient: 'Luigi Gallo',
  email: 'luigi.gallo@email.it',
  type: 'Risonanza magnetica ginocchio',
  doctor: 'Dr. A. Neri',
  date: '2026-03-08',
  status: 'Inviato'
}, {
  id: 'REP-2026-006',
  patient: 'Francesca Moretti',
  email: 'f.moretti@email.it',
  type: 'TAC addome',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-08',
  status: 'Da refertare'
}, {
  id: 'REP-2026-007',
  patient: 'Roberto Colombo',
  email: 'r.colombo@email.it',
  type: 'Risonanza magnetica colonna',
  doctor: 'Dr. A. Neri',
  date: '2026-03-07',
  status: 'Refertato'
}, {
  id: 'REP-2026-008',
  patient: 'Chiara Rizzo',
  email: 'c.rizzo@email.it',
  type: 'Radiografia torace',
  doctor: 'Dr. M. Gialli',
  date: '2026-03-07',
  status: 'Da refertare'
}, {
  id: 'REP-2026-009',
  patient: 'Davide Fontana',
  email: 'd.fontana@email.it',
  type: 'Ecografia pelvi',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-06',
  status: 'Refertato'
}, {
  id: 'REP-2026-010',
  patient: 'Sara Lombardi',
  email: 's.lombardi@email.it',
  type: 'TAC encefalo',
  doctor: 'Dr. A. Neri',
  date: '2026-03-06',
  status: 'Refertato'
}, {
  id: 'REP-2026-011',
  patient: 'Andrea Barbieri',
  email: 'a.barbieri@email.it',
  type: 'Risonanza magnetica encefalo',
  doctor: 'Dr. M. Gialli',
  date: '2026-03-05',
  status: 'Da refertare'
}, {
  id: 'REP-2026-012',
  patient: 'Valentina Costa',
  email: 'v.costa@email.it',
  type: 'Radiografia colonna vertebrale',
  doctor: 'Dr. S. Verdi',
  date: '2026-03-05',
  status: 'Refertato'
}];
const uniqueExamTypes = Array.from(new Set(mockReports.map(r => r.type))).sort();
export function Dashboard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const {
    role
  } = useRole();
  const CURRENT_USER = role === 'medico' ? 'Dr. S. Verdi' : 'Superadmin';
  const isMedico = role === 'medico';
  // Machine types operable by the logged-in medico (Dr. S. Verdi)
  const MEDICO_MACHINE_TYPES = ['Risonanza Magnetica', 'TAC', 'Ecografia', 'Radiografia'];
  // --- Search, filter, sort state ---
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const [typeFilter, setTypeFilter] = useState('Tutti');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  // --- Pagination ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, typeFilter, sortKey, sortDirection]);
  // --- Filtered + sorted data ---
  const dashboardReports = useMemo(() => {
    if (role === 'medico') {
      return mockReports.filter(r => r.doctor === CURRENT_USER);
    }
    return mockReports;
  }, [role, CURRENT_USER]);
  const dashboardTimeline = useMemo(() => {
    if (role === 'medico') {
      return mockTimeline.filter(e => e.doctor === CURRENT_USER);
    }
    return mockTimeline;
  }, [role, CURRENT_USER]);
  const dashboardMachines = useMemo(() => {
    if (isMedico) {
      return mockMachines.filter(m => MEDICO_MACHINE_TYPES.includes(m.type));
    }
    return mockMachines;
  }, [isMedico]);
  const processedReports = useMemo(() => {
    let result = [...dashboardReports];
    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(r => r.id.toLowerCase().includes(q) || r.patient.toLowerCase().includes(q) || r.doctor.toLowerCase().includes(q) || r.type.toLowerCase().includes(q));
    }
    // Status filter
    if (statusFilter !== 'Tutti') {
      result = result.filter(r => r.status === statusFilter);
    }
    // Type filter
    if (typeFilter !== 'Tutti') {
      result = result.filter(r => r.type === typeFilter);
    }
    // Sort
    if (sortKey && sortDirection) {
      result.sort((a, b) => {
        const aVal = a[sortKey].toLowerCase();
        const bVal = b[sortKey].toLowerCase();
        if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [dashboardReports, searchQuery, statusFilter, typeFilter, sortKey, sortDirection]);
  const totalPages = Math.ceil(processedReports.length / itemsPerPage);
  const paginatedReports = processedReports.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const handleSort = (key: SortKey) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortKey(null);
      setSortDirection(null);
    }
  };
  // --- Modals ---
  const [viewReportModal, setViewReportModal] = useState<Report | null>(null);
  const [resendReportModal, setResendReportModal] = useState<Report | null>(null);
  // --- Toast ---
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  // --- Scroll gradient ---
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState]);
  // --- Handlers ---
  const handleExamClick = (id: number) => navigate(`/exams/report/${id}`);
  const handleMachineClick = () => {
    if (role === 'superadmin') {
      navigate('/machines');
    }
  };
  const handleViewReport = (report: Report) => setViewReportModal(report);
  const handleResendClick = (report: Report) => setResendReportModal(report);
  const confirmResend = () => {
    const isSuccess = Math.random() > 0.2;
    setResendReportModal(null);
    setToastMessage(isSuccess ? {
      type: 'success',
      text: 'Referto inviato correttamente al paziente.'
    } : {
      type: 'error',
      text: "Errore durante l'invio del referto. Riprovare."
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  // --- Render ---
  return <div className="space-y-8 relative w-full min-w-0" data-id="element-649">
      {/* Header */}
      <div data-id="element-650">
        <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-651">
          Dashboard
        </h1>
        <p className="text-sm md:text-body text-text-secondary" data-id="element-652">
          Panoramica operativa del modulo diagnostico.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 min-w-0 overflow-hidden" data-id="element-653">
        <KpiCard title="Esami di oggi" value={dashboardTimeline.length} icon={<ActivityIcon size={24} data-id="element-655" />} delay={0.1} data-id="element-654" />
        <KpiCard title="In attesa di referto" value={dashboardReports.filter(r => r.status === 'Da refertare').length} icon={<ClockIcon size={24} data-id="element-657" />} iconBgColor="bg-status-warning-bg" iconColor="text-status-warning" delay={0.2} data-id="element-656" />
        <KpiCard title="Macchinari attivi" value={dashboardMachines.filter(m => m.status === 'Connesso').length} icon={<CpuIcon size={24} data-id="element-659" />} iconBgColor="bg-status-info-bg" iconColor="text-status-info" delay={0.3} data-id="element-658" />
        <KpiCard title="Referti in sospeso" value={dashboardReports.filter(r => r.status === 'Da refertare' || r.status === 'In corso').length} icon={<FileTextIcon size={24} data-id="element-661" />} iconBgColor="bg-medical-orange-50" iconColor="text-medical-orange" delay={0.4} data-id="element-660" />
      </div>

      {/* Timeline */}
      <section data-id="element-662">
        <h2 className="text-xl md:text-section-title mb-4 text-text-primary font-semibold" data-id="element-663">
          Programma di oggi
        </h2>
        <div className="relative overflow-hidden group" data-id="element-664">
          <div className="absolute left-0 top-0 bottom-0 w-12 md:w-16 z-10 pointer-events-none transition-opacity duration-300 rounded-l-medical-lg" style={{
          background: 'linear-gradient(to right, #F6F8F9, transparent)',
          opacity: canScrollLeft ? 1 : 0
        }} data-id="element-665" />
          <div className="absolute right-0 top-0 bottom-0 w-12 md:w-16 z-10 pointer-events-none transition-opacity duration-300 rounded-r-medical-lg" style={{
          background: 'linear-gradient(to left, #F6F8F9, transparent)',
          opacity: canScrollRight ? 1 : 0
        }} data-id="element-666" />

          {/* Desktop Navigation Arrows */}
          <button onClick={() => scrollRef.current?.scrollBy({
          left: -300,
          behavior: 'smooth'
        })} className={`hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 items-center justify-center bg-white/80 backdrop-blur-sm border border-clinical-gray-200 rounded-full shadow-sm hover:bg-white hover:shadow-card transition-all text-text-secondary hover:text-text-primary ${canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-label="Scorri a sinistra" data-id="element-667">
            <ChevronLeftIcon size={18} data-id="element-668" />
          </button>
          <button onClick={() => scrollRef.current?.scrollBy({
          left: 300,
          behavior: 'smooth'
        })} className={`hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 z-20 w-8 h-8 items-center justify-center bg-white/80 backdrop-blur-sm border border-clinical-gray-200 rounded-full shadow-sm hover:bg-white hover:shadow-card transition-all text-text-secondary hover:text-text-primary ${canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} aria-label="Scorri a destra" data-id="element-669">
            <ChevronRightIcon size={18} data-id="element-670" />
          </button>

          <div ref={scrollRef} onScroll={updateScrollState} className="flex gap-4 overflow-x-auto pb-4 snap-x no-scrollbar relative z-0" data-id="element-671">
            {dashboardTimeline.map((exam, index) => <motion.div key={exam.id} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.2 + index * 0.1
          }} className="snap-start shrink-0 rounded-medical-lg" style={{
            willChange: 'transform'
          }} data-id="element-672">
                <ExamCard {...exam} data-id="element-673" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Bottom grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8 min-w-0 overflow-hidden" data-id="element-674">
        {/* Machine Status */}
        <section className="min-w-0 overflow-hidden lg:col-span-4 xl:col-span-4" data-id="element-675">
          <h2 className="text-xl md:text-section-title mb-4 text-text-primary font-semibold" data-id="element-676">
            Stato macchinari
          </h2>
          <div className="medical-card p-0 overflow-hidden" data-id="element-677">
            {dashboardMachines.map(machine => <div key={machine.id} onClick={handleMachineClick} className={`p-4 border-b border-clinical-gray-100 last:border-0 flex items-center justify-between transition-colors group ${role === 'superadmin' ? 'hover:bg-clinical-gray-50 cursor-pointer' : ''}`} data-id="element-678">
                <div className="min-w-0 pr-2" data-id="element-679">
                  <p className={`text-sm md:text-body font-medium text-text-primary truncate transition-colors ${role === 'superadmin' ? 'group-hover:text-medical-green' : ''}`} data-id="element-680">
                    {machine.name}
                  </p>
                  <p className="text-xs md:text-small text-text-secondary truncate" data-id="element-681">
                    {machine.type}
                  </p>
                </div>
                <div className="text-right flex flex-col items-end gap-1 shrink-0" data-id="element-682">
                  <MachineStatusIndicator status={machine.status} data-id="element-683" />
                  <span className="text-[10px] md:text-xs text-text-tertiary" data-id="element-684">
                    {machine.lastSync}
                  </span>
                </div>
              </div>)}
          </div>
        </section>

        {/* Latest Reports — Custom table */}
        <section className="min-w-0 overflow-hidden lg:col-span-8 xl:col-span-8" data-id="element-685">
          <h2 className="text-xl md:text-section-title mb-4 text-text-primary font-semibold" data-id="element-686">
            Ultimi referti
          </h2>

          {/* Search + Filters bar */}
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4" data-id="element-687">
            {/* Search — shorter width */}
            <div className="relative w-full sm:w-auto sm:min-w-[180px] sm:max-w-[260px] sm:flex-1" data-id="element-688">
              <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-689" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca paziente, medico, ID..." className="medical-input pl-9 text-sm h-9" data-id="element-690" />
            </div>
            {/* Status filter */}
            <div className="relative w-full sm:w-auto sm:shrink-0" data-id="element-691">
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full" data-id="element-692">
                <option value="Tutti" data-id="element-693">Stato: Tutti</option>
                <option value="Da refertare" data-id="element-694">Da refertare</option>
                <option value="Refertato" data-id="element-695">Refertato</option>
                <option value="Inviato" data-id="element-696">Inviato</option>
              </select>
              <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-697" />
            </div>
            {/* Type filter */}
            <div className="relative w-full sm:w-auto sm:shrink-0" data-id="element-698">
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full" data-id="element-699">
                <option value="Tutti" data-id="element-700">Tipo esame: Tutti</option>
                {uniqueExamTypes.map(t => <option key={t} value={t} data-id="element-701">
                    {t}
                  </option>)}
              </select>
              <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-702" />
            </div>
          </div>

          {/* Desktop table */}
          {!isMobile ? <div className="w-full bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" style={{
          overflowX: 'auto',
          overflowY: 'visible'
        }} data-id="element-703">
              <table className="w-full text-left border-collapse" style={{
            tableLayout: 'fixed',
            minWidth: '600px'
          }} data-id="element-704">
                <colgroup data-id="element-705">
                  {isMedico ? <>
                      <col style={{
                  width: '16%'
                }} data-id="element-706" />
                      <col style={{
                  width: '20%'
                }} data-id="element-707" />
                      <col style={{
                  width: '30%'
                }} data-id="element-708" />
                      <col style={{
                  width: '18%'
                }} data-id="element-709" />
                      <col style={{
                  width: '16%'
                }} data-id="element-710" />
                    </> : <>
                      <col style={{
                  width: '14%'
                }} data-id="element-711" />
                      <col style={{
                  width: '16%'
                }} data-id="element-712" />
                      <col style={{
                  width: '24%'
                }} data-id="element-713" />
                      <col style={{
                  width: '14%'
                }} data-id="element-714" />
                      <col style={{
                  width: '16%'
                }} data-id="element-715" />
                      <col style={{
                  width: '16%'
                }} data-id="element-716" />
                    </>}
                </colgroup>
                <thead data-id="element-717">
                  <tr className="bg-clinical-gray border-b border-clinical-gray-200" data-id="element-718">
                    <th className="px-4 py-3" data-id="element-719">
                      <SortableHeader label="ID Referto" sortKey="id" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} data-id="element-720" />
                    </th>
                    <th className="px-4 py-3" data-id="element-721">
                      <SortableHeader label="Paziente" sortKey="patient" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} data-id="element-722" />
                    </th>
                    <th className="px-4 py-3" data-id="element-723">
                      <SortableHeader label="Tipo Esame" sortKey="type" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} data-id="element-724" />
                    </th>
                    {!isMedico && <th className="px-4 py-3" data-id="element-725">
                        <SortableHeader label="Medico" sortKey="doctor" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} data-id="element-726" />
                      </th>}
                    <th className="px-4 py-3" data-id="element-727">
                      <SortableHeader label="Stato" sortKey="status" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} data-id="element-728" />
                    </th>
                    <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap text-right" data-id="element-729">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-730">
                  {paginatedReports.length === 0 ? <tr data-id="element-731">
                      <td colSpan={isMedico ? 5 : 6} className="px-4 py-8 text-center text-text-secondary text-body" data-id="element-732">
                        Nessun referto trovato.
                      </td>
                    </tr> : paginatedReports.map(item => <tr key={item.id} className="border-b border-clinical-gray-100 last:border-0 hover:bg-clinical-gray/30 transition-colors" data-id="element-733">
                        <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-734">
                          <TruncatedCell text={item.id} data-id="element-735" />
                        </td>
                        <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-736">
                          <TruncatedCell text={item.patient} data-id="element-737" />
                        </td>
                        <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-738">
                          <TruncatedCell text={item.type} data-id="element-739" />
                        </td>
                        {!isMedico && <td className="px-4 py-3 text-body text-text-primary overflow-hidden" data-id="element-740">
                            <TruncatedCell text={item.doctor} data-id="element-741" />
                          </td>}
                        <td className="px-4 py-3" data-id="element-742">
                          <StatusBadge status={item.status} data-id="element-743" />
                        </td>
                        <td className="px-4 py-3" data-id="element-744">
                          <div className="flex items-center justify-end gap-1" data-id="element-745">
                            <div className="relative group/action" data-id="element-746">
                              <button onClick={() => handleViewReport(item)} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza Referto" data-id="element-747">
                                <EyeIcon size={16} data-id="element-748" />
                              </button>
                              <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
                        zIndex: 9999
                      }} ref={el => {
                        if (el) {
                          const btn = el.previousElementSibling as HTMLElement;
                          if (btn) {
                            const rect = btn.getBoundingClientRect();
                            el.style.top = `${rect.top - el.offsetHeight - 6}px`;
                            el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
                          }
                        }
                      }} data-id="element-749">
                                Visualizza il dettaglio del referto
                              </div>
                            </div>
                            <div className="relative group/action" data-id="element-750">
                              <button onClick={() => (item.status === 'Refertato' || item.status === 'Inviato') && handleResendClick(item)} disabled={item.status !== 'Refertato' && item.status !== 'Inviato'} className={`p-1.5 rounded transition-colors ${item.status === 'Refertato' || item.status === 'Inviato' ? 'text-text-secondary hover:text-medical-green hover:bg-medical-green-50' : 'text-clinical-gray-300 cursor-not-allowed'}`} aria-label="Reinvia Referto" data-id="element-751">
                                <SendIcon size={16} data-id="element-752" />
                              </button>
                              <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
                        zIndex: 9999
                      }} ref={el => {
                        if (el) {
                          const btn = el.previousElementSibling as HTMLElement;
                          if (btn) {
                            const rect = btn.getBoundingClientRect();
                            el.style.top = `${rect.top - el.offsetHeight - 6}px`;
                            el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
                          }
                        }
                      }} data-id="element-753">
                                Reinvia il referto al paziente
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div> : <div className="space-y-3" data-id="element-754">
              {paginatedReports.length === 0 ? <div className="p-6 text-center text-text-secondary text-body bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-755">
                  Nessun referto trovato.
                </div> : paginatedReports.map(item => <div key={item.id} className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 overflow-hidden" data-id="element-756">
                    <div className="p-4 space-y-3" data-id="element-757">
                      <div className="flex justify-between items-start" data-id="element-758">
                        <div className="min-w-0 mr-3" data-id="element-759">
                          <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-760">
                            Paziente
                          </span>
                          <span className="text-sm font-medium text-text-primary truncate block" data-id="element-761">
                            {item.patient}
                          </span>
                        </div>
                        <StatusBadge status={item.status} data-id="element-762" />
                      </div>
                      <div className={`grid ${isMedico ? 'grid-cols-1' : 'grid-cols-2'} gap-2`} data-id="element-763">
                        <div className="min-w-0" data-id="element-764">
                          <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-765">
                            Tipo Esame
                          </span>
                          <span className="text-sm text-text-primary truncate block" data-id="element-766">
                            {item.type}
                          </span>
                        </div>
                        {!isMedico && <div className="min-w-0" data-id="element-767">
                            <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-0.5" data-id="element-768">
                              Medico
                            </span>
                            <span className="text-sm text-text-primary truncate block" data-id="element-769">
                              {item.doctor}
                            </span>
                          </div>}
                      </div>
                      <div className="pt-3 mt-3 border-t border-clinical-gray-100 flex justify-end gap-3" data-id="element-770">
                        <button onClick={() => handleViewReport(item)} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-medical-green transition-colors" data-id="element-771">
                          <EyeIcon size={16} data-id="element-772" /> Visualizza
                        </button>
                        <button onClick={() => (item.status === 'Refertato' || item.status === 'Inviato') && handleResendClick(item)} disabled={item.status !== 'Refertato' && item.status !== 'Inviato'} className={`flex items-center gap-1.5 text-sm transition-colors ${item.status === 'Refertato' || item.status === 'Inviato' ? 'text-text-secondary hover:text-medical-green' : 'text-clinical-gray-300'}`} data-id="element-773">
                          <SendIcon size={16} data-id="element-774" /> Reinvia
                        </button>
                      </div>
                    </div>
                  </div>)}
            </div>}

          {/* Pagination */}
          {totalPages > 1 && <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 px-1 gap-2" data-id="element-775">
              <span className="text-sm text-text-secondary" data-id="element-776">
                {processedReports.length} risultati — Pagina {currentPage} di{' '}
                {totalPages}
              </span>
              <div className="flex items-center gap-2" data-id="element-777">
                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-778">
                  <ChevronLeftIcon size={16} data-id="element-779" />
                </button>
                <div className="flex items-center gap-1" data-id="element-780">
                  {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors ${currentPage === page ? 'bg-medical-green text-white border border-medical-green' : 'border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50'}`} data-id="element-781">
                      {page}
                    </button>)}
                </div>
                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-8 h-8 flex items-center justify-center rounded border border-clinical-gray-200 text-text-secondary hover:bg-clinical-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-782">
                  <ChevronRightIcon size={16} data-id="element-783" />
                </button>
              </div>
            </div>}

          {/* No results + no pagination */}
          {processedReports.length === 0 && (searchQuery || statusFilter !== 'Tutti' || typeFilter !== 'Tutti') && <div className="mt-3 text-center" data-id="element-784">
                <button onClick={() => {
            setSearchQuery('');
            setStatusFilter('Tutti');
            setTypeFilter('Tutti');
          }} className="text-sm text-medical-green hover:underline transition-colors" data-id="element-785">
                  Cancella tutti i filtri
                </button>
              </div>}
        </section>
      </div>

      {/* View Report Modal */}
      <ModalDialog isOpen={!!viewReportModal} onClose={() => setViewReportModal(null)} title="Dettaglio Referto" maxWidth="max-w-3xl" footer={<button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setViewReportModal(null)} data-id="element-787">
            Chiudi
          </button>} data-id="element-786">
        {viewReportModal && <div className="space-y-6" data-id="element-788">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-789">
              <div className="space-y-4" data-id="element-790">
                <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wider border-b border-clinical-gray-100 pb-2" data-id="element-791">
                  Informazioni Generali
                </h4>
                <div className="grid grid-cols-2 gap-4" data-id="element-792">
                  <div data-id="element-793">
                    <span className="text-xs text-text-tertiary block mb-1" data-id="element-794">
                      ID Referto
                    </span>
                    <span className="text-sm font-medium text-text-primary" data-id="element-795">
                      {viewReportModal.id}
                    </span>
                  </div>
                  <div data-id="element-796">
                    <span className="text-xs text-text-tertiary block mb-1" data-id="element-797">
                      Data
                    </span>
                    <span className="text-sm font-medium text-text-primary" data-id="element-798">
                      {viewReportModal.date}
                    </span>
                  </div>
                  <div data-id="element-799">
                    <span className="text-xs text-text-tertiary block mb-1" data-id="element-800">
                      Tipo Esame
                    </span>
                    <span className="text-sm font-medium text-text-primary" data-id="element-801">
                      {viewReportModal.type}
                    </span>
                  </div>
                  <div data-id="element-802">
                    <span className="text-xs text-text-tertiary block mb-1" data-id="element-803">
                      Stato
                    </span>
                    <StatusBadge status={viewReportModal.status} data-id="element-804" />
                  </div>
                </div>
              </div>
              <div className="space-y-4" data-id="element-805">
                <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wider border-b border-clinical-gray-100 pb-2" data-id="element-806">
                  Paziente e Medico
                </h4>
                <div className="grid grid-cols-1 gap-4" data-id="element-807">
                  <div data-id="element-808">
                    <span className="text-xs text-text-tertiary block mb-1" data-id="element-809">
                      Paziente
                    </span>
                    <span className="text-sm font-medium text-text-primary" data-id="element-810">
                      {viewReportModal.patient}
                    </span>
                  </div>
                  <div data-id="element-811">
                    <span className="text-xs text-text-tertiary block mb-1" data-id="element-812">
                      Email Paziente
                    </span>
                    <div className="flex items-center gap-2 text-sm text-text-primary" data-id="element-813">
                      <MailIcon size={14} className="text-text-tertiary" data-id="element-814" />
                      {viewReportModal.email}
                    </div>
                  </div>
                  <div data-id="element-815">
                    <span className="text-xs text-text-tertiary block mb-1" data-id="element-816">
                      Medico Refertatore
                    </span>
                    <span className="text-sm font-medium text-text-primary" data-id="element-817">
                      {viewReportModal.doctor}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <div data-id="element-818">
              <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wider border-b border-clinical-gray-100 pb-2 mb-4" data-id="element-819">
                Anteprima Documento
              </h4>
              <div className="bg-clinical-gray-50 border border-clinical-gray-200 rounded-medical-lg h-64 flex flex-col items-center justify-center text-text-tertiary" data-id="element-820">
                <FileIcon size={48} className="mb-3 opacity-50" data-id="element-821" />
                <p className="text-sm font-medium text-text-secondary" data-id="element-822">
                  Anteprima PDF non disponibile
                </p>
                <p className="text-xs mt-1" data-id="element-823">
                  Il documento completo è archiviato nel sistema PACS.
                </p>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Resend Report Modal */}
      <ModalDialog isOpen={!!resendReportModal} onClose={() => setResendReportModal(null)} title="Reinvia Referto" footer={<>
            <button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setResendReportModal(null)} data-id="element-825">
              Annulla
            </button>
            <button className="medical-btn-primary w-full sm:w-auto" onClick={confirmResend} data-id="element-826">
              <SendIcon size={18} className="mr-2" data-id="element-827" /> Reinvia referto
            </button>
          </>} data-id="element-824">
        {resendReportModal && <div className="space-y-6" data-id="element-828">
            <div className="bg-status-info-bg border border-status-info/20 rounded-medical p-4 flex items-start gap-3" data-id="element-829">
              <MailIcon className="text-status-info shrink-0 mt-0.5" size={20} data-id="element-830" />
              <div data-id="element-831">
                <p className="text-sm text-text-primary font-medium mb-1" data-id="element-832">
                  Stai per reinviare il referto al paziente.
                </p>
                <p className="text-sm text-text-secondary" data-id="element-833">
                  Un'email contenente il link sicuro per scaricare il referto
                  verrà inviata all'indirizzo registrato.
                </p>
              </div>
            </div>
            <div className="bg-white border border-clinical-gray-200 rounded-medical-lg p-4 space-y-3" data-id="element-834">
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-835">
                <span className="text-sm text-text-secondary" data-id="element-836">Paziente</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-837">
                  {resendReportModal.patient}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-838">
                <span className="text-sm text-text-secondary" data-id="element-839">
                  Email di destinazione
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-840">
                  {resendReportModal.email}
                </span>
              </div>
              <div className="flex justify-between items-center border-b border-clinical-gray-100 pb-3" data-id="element-841">
                <span className="text-sm text-text-secondary" data-id="element-842">Esame</span>
                <span className="text-sm font-medium text-text-primary" data-id="element-843">
                  {resendReportModal.type}
                </span>
              </div>
              <div className="flex justify-between items-center pt-1" data-id="element-844">
                <span className="text-sm text-text-secondary" data-id="element-845">
                  Documento allegato
                </span>
                <div className="flex items-center gap-1.5 text-sm font-medium text-medical-green" data-id="element-846">
                  <FileIcon size={16} data-id="element-847" /> {resendReportModal.id}.pdf
                </div>
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Toast */}
      <AnimatePresence data-id="element-848">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border w-[calc(100%-3rem)] sm:w-auto ${toastMessage.type === 'success' ? 'bg-white border-status-success/20 text-text-primary' : 'bg-white border-status-error/20 text-text-primary'}`} data-id="element-849">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-850" /> : <XCircleIcon className="text-status-error shrink-0" size={20} data-id="element-851" />}
            <span className="text-sm font-medium" data-id="element-852">{toastMessage.text}</span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-853">
              <XIcon size={16} data-id="element-854" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}